// In-world FX: authored sprite sheets (client/assets/fx/, painted kit — never procedural shapes) played by one
// instanced billboard system per sheet. A particle = position, velocity, gravity, spin, size curve, life, a cell (or a
// flipbook run of cells). Cheap enough for the 4 GB Chromebook: one draw call per sheet, ≤ 512 live particles each.

import * as THREE from "three";

export interface SheetInfo { file: string; cols: number; rows: number; frames?: number; }
interface Part { x: number; y: number; z: number; vx: number; vy: number; vz: number; g: number; drag: number; rot: number; vr: number; s0: number; s1: number; life: number; age: number; cell: number; anim: boolean; alpha: number; }

const VERT = /* glsl */ `
attribute vec3 iPos; attribute vec4 iData; // size, rot, cell, alpha
uniform vec2 uGrid; varying vec2 vUv; varying float vA;
void main() {
  vec4 mv = modelViewMatrix * vec4(iPos, 1.0);
  float c = cos(iData.y), s = sin(iData.y);
  vec2 p = vec2(position.x * c - position.y * s, position.x * s + position.y * c) * iData.x;
  mv.xy += p;
  gl_Position = projectionMatrix * mv;
  float cell = iData.z; float col = mod(cell, uGrid.x); float row = floor(cell / uGrid.x);
  vUv = (vec2(col, uGrid.y - 1.0 - row) + uv) / uGrid; vA = iData.w;
}`;
const FRAG = /* glsl */ `
uniform sampler2D uMap; varying vec2 vUv; varying float vA;
void main() {
  vec4 t = texture2D(uMap, vUv); if (t.a * vA < 0.02) discard; gl_FragColor = vec4(t.rgb, t.a * vA);
#include <colorspace_fragment>
}`;

export class Sheet {
  mesh: THREE.InstancedMesh;
  private parts: Part[] = [];
  private pos: THREE.InstancedBufferAttribute; private data: THREE.InstancedBufferAttribute;
  constructor(tex: THREE.Texture, public info: SheetInfo, private max = 512) {
    tex.colorSpace = THREE.SRGBColorSpace;
    const geo = new THREE.InstancedBufferGeometry().copy(new THREE.PlaneGeometry(1, 1) as any) as THREE.InstancedBufferGeometry;
    this.pos = new THREE.InstancedBufferAttribute(new Float32Array(max * 3), 3); this.pos.setUsage(THREE.DynamicDrawUsage);
    this.data = new THREE.InstancedBufferAttribute(new Float32Array(max * 4), 4); this.data.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("iPos", this.pos); geo.setAttribute("iData", this.data);
    const mat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false,
      uniforms: { uMap: { value: tex }, uGrid: { value: new THREE.Vector2(info.cols, info.rows) } } });
    this.mesh = new THREE.InstancedMesh(geo, mat, max); this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 5;
  }
  emit(p: Partial<Part> & { x: number; y: number; z: number }) {
    if (this.parts.length >= this.max) this.parts.shift();
    this.parts.push({ vx: 0, vy: 0, vz: 0, g: 0, drag: 0, rot: 0, vr: 0, s0: 0.2, s1: 0.2, life: 1, age: 0, cell: 0, anim: false, alpha: 1, ...p });
  }
  update(dt: number) {
    let n = 0;
    const cells = this.info.frames ?? this.info.cols * this.info.rows;
    this.parts = this.parts.filter((p) => (p.age += dt) < p.life);
    for (const p of this.parts) {
      p.vy += p.g * dt; const d = Math.exp(-p.drag * dt); p.vx *= d; p.vz *= d; p.vy *= p.g ? 1 : d;
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; p.rot += p.vr * dt;
      if (p.y < 0.02 && p.g < 0) { p.y = 0.02; p.vy *= -0.2; p.vx *= 0.6; p.vz *= 0.6; p.vr *= 0.5; }
      const k = p.age / p.life;
      this.pos.setXYZ(n, p.x, p.y, p.z);
      const cell = p.anim ? Math.min(cells - 1, Math.floor(k * cells)) : p.cell;
      const fade = k > 0.75 ? 1 - (k - 0.75) / 0.25 : 1;
      this.data.setXYZW(n, p.s0 + (p.s1 - p.s0) * k, p.rot, cell, p.alpha * fade);
      n++;
    }
    this.mesh.count = n; this.pos.needsUpdate = true; this.data.needsUpdate = true;
  }
  get cells() { return this.info.frames ?? this.info.cols * this.info.rows; }
}

const rnd = (a: number, b: number) => a + Math.random() * (b - a);

/** the named effects the game calls */
export class Fx {
  group = new THREE.Group();
  sheets: Record<string, Sheet> = {};
  add(name: string, s: Sheet) { this.sheets[name] = s; this.group.add(s.mesh); }

  /** a caught Sneak's costume bursts: the equipped reveal effect */
  burst(kind: string, x: number, y: number, z: number, n = 72) {
    const s = this.sheets[kind] ?? this.sheets.fx_confetti; if (!s) return;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, up = rnd(4, 9), out = rnd(1.4, 5);
      s.emit({ x, y, z, vx: Math.cos(a) * out, vy: up, vz: Math.sin(a) * out, g: -9, drag: 1.4, vr: rnd(-9, 9), rot: rnd(0, 6.3), s0: rnd(0.2, 0.34), s1: rnd(0.16, 0.28), life: rnd(1.4, 2.4), cell: Math.floor(Math.random() * s.cells) });
    }
    this.puff(x, z, 8, true);
  }
  /** the blunder "?" pops in above a head and floats */
  question(x: number, y: number, z: number) {
    const s = this.sheets.fx_question; if (!s) return;
    s.emit({ x, y, z, vy: 0.25, s0: 0.35, s1: 0.5, life: 2.0, anim: false, cell: s.cells - 1 });
    s.emit({ x, y, z, vy: 0.25, s0: 0.1, s1: 0.45, life: 0.18, anim: true });
  }
  /** a carried capsule's glint */
  glint(x: number, y: number, z: number) {
    const s = this.sheets.fx_sparkle; if (!s) return;
    for (let i = 0; i < 3; i++) s.emit({ x: x + rnd(-0.15, 0.15), y: y + rnd(-0.1, 0.25), z: z + rnd(-0.15, 0.15), vy: 0.3, s0: 0.05, s1: rnd(0.16, 0.26), vr: rnd(-2, 2), life: rnd(0.4, 0.7), cell: Math.floor(Math.random() * s.cells) });
  }
  puff(x: number, z: number, n = 6, big = false) {
    const s = this.sheets.fx_puff; if (!s) return;
    for (let i = 0; i < n; i++) { const a = Math.random() * Math.PI * 2; s.emit({ x, y: 0.15, z, vx: Math.cos(a) * rnd(0.4, 1.4), vy: rnd(0.2, 0.7), vz: Math.sin(a) * rnd(0.4, 1.4), drag: 2.5, s0: big ? 0.5 : 0.25, s1: big ? 1.1 : 0.6, life: rnd(0.5, 0.9), anim: true }); }
  }
  /** the Popper ray: a short beam of the equipped ray's particles from the Inspector to the target */
  ray(kind: string, ax: number, ay: number, az: number, bx: number, by: number, bz: number) {
    const s = this.sheets[kind] ?? this.sheets.fx_confetti; if (!s) return;
    const n = 26;
    for (let i = 0; i < n; i++) {
      const k = i / n;
      s.emit({ x: ax + (bx - ax) * k, y: ay + (by - ay) * k, z: az + (bz - az) * k, vx: rnd(-0.5, 0.5), vy: rnd(0, 1.2), vz: rnd(-0.5, 0.5), g: -4, s0: 0.14, s1: 0.06, vr: rnd(-6, 6), life: 0.25 + k * 0.35, cell: Math.floor(Math.random() * s.cells) });
    }
  }
  update(dt: number) { for (const s of Object.values(this.sheets)) s.update(dt); }
}
